/**
 * Production-Grade API Client
 * 
 * Features:
 * - JWT authentication with automatic token refresh
 * - Request/response interceptors for error handling
 * - Timeout protection
 * - Retry logic for network failures
 * - AWS-compatible configuration
 */

import axios from 'axios';
import { getDeviceFingerprint } from './deviceFingerprint';

// Environment-based API URL (AWS-compatible)
const getApiUrl = () => {
    // Priority: Environment variable > Default
    if (process.env.REACT_APP_API_URL) {
        // Remove trailing slash to prevent double slashes
        return process.env.REACT_APP_API_URL.replace(/\/$/, '');
    }

    // Default to localhost in development
    if (process.env.NODE_ENV === 'development') {
        return 'http://localhost:8000';
    }

    // Production fallback: point to the api subdomain
    return 'https://api.teachgenie.ai';
};

export const API_BASE_URL = getApiUrl();

const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// Issue #196: the CSRF cookie is deliberately the one NOT marked HttpOnly
// (see backend's csrf_cookie.py) — its whole purpose is being readable here
// so it can be echoed back as a header, proving this request came from JS
// running on a page that could read teachgenie.ai's own cookies.
const getCsrfTokenFromCookie = () => {
    const match = document.cookie.match(/(?:^|; )tg_csrf_token=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
};

// Create axios instance with production defaults
export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 300000, // 5 minutes timeout
    headers: {
        'Content-Type': 'application/json',
    },
    // Issue #196: auth lives in an HttpOnly cookie now, not a header this
    // code attaches itself — the browser only sends it when this is true.
    withCredentials: true,
});

// Track if we're currently refreshing token to prevent multiple refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });

    failedQueue = [];
};

/**
 * REQUEST INTERCEPTOR
 * Attaches the CSRF header (cookie auth needs it; see csrf_cookie.py) and
 * the device fingerprint. The actual auth token is a cookie the browser
 * attaches on its own — nothing to read from localStorage here anymore.
 */
apiClient.interceptors.request.use(
    async (config) => {
        const method = (config.method || 'get').toLowerCase();
        if (UNSAFE_METHODS.has(method) && !config.headers['X-CSRF-Token']) {
            const csrfToken = getCsrfTokenFromCookie();
            if (csrfToken) {
                config.headers['X-CSRF-Token'] = csrfToken;
            }
        }

        // Device fingerprint for session binding (issue #196) — see
        // deviceFingerprint.js. Cached after the first call, so this only
        // costs real time once per page load, not per request. Never blocks
        // the request if it fails; the header is just omitted.
        const fingerprint = await getDeviceFingerprint();
        if (fingerprint) {
            config.headers['X-Device-Fingerprint'] = fingerprint;
        }

        // Add request timestamp for debugging
        config.metadata = { startTime: new Date() };

        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

/**
 * RESPONSE INTERCEPTOR
 * Handles token refresh and error responses
 */
apiClient.interceptors.response.use(
    (response) => {
        // Log response time in development
        if (process.env.NODE_ENV === 'development') {
            const requestUrl = response?.config?.url || '';
            const isNoisyAuthPoll = requestUrl.includes('/api/v1/auth/me');
            const endTime = new Date();
            const duration = endTime - response.config.metadata.startTime;
            if (!isNoisyAuthPoll) {
                console.log(`API ${response.config.method.toUpperCase()} ${response.config.url}: ${duration}ms`);
            }
        }

        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Network error (no response from server)
        if (!error.response) {
            console.error('Network error:', error.message);
            return Promise.reject({
                message: 'Network error. Please check your internet connection.',
                isNetworkError: true,
            });
        }

        // Handle 401 Unauthorized - Token expired or invalid
        if (error.response.status === 401 && !originalRequest._retry) {
            // Prevent infinite loops
            if (originalRequest.url.includes('/auth/refresh')) {
                // Refresh token itself failed
                handleAuthFailure(error);
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            // If already refreshing, queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(() => apiClient(originalRequest))
                    .catch(err => Promise.reject(err));
            }

            isRefreshing = true;

            try {
                // Issue #196: the refresh token lives in the tg_refresh_token
                // HttpOnly cookie now — nothing to read from localStorage or
                // put in the body. withCredentials is set explicitly because
                // this is a bare axios.post, not the apiClient instance (whose
                // default doesn't apply here).
                const csrfToken = getCsrfTokenFromCookie();
                await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {}, {
                    withCredentials: true,
                    headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
                });

                // Process queued requests — the new cookie is already stored
                // by the browser, so they just need to retry.
                processQueue(null);

                return apiClient(originalRequest);
            } catch (refreshError) {
                // Refresh failed
                processQueue(refreshError);
                handleAuthFailure(refreshError);
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Handle 403 Forbidden
        if (error.response.status === 403) {
            console.error('Access forbidden:', error.response.data);
            return Promise.reject({
                message: error.response.data.detail || 'You do not have permission to perform this action.',
                statusCode: 403,
            });
        }

        // Handle 429 Too Many Requests
        if (error.response.status === 429) {
            return Promise.reject({
                message: 'Too many requests. Please wait a moment and try again.',
                statusCode: 429,
            });
        }

        // Handle 500 Server Errors
        if (error.response.status >= 500) {
            console.error('Server error:', error.response.data);
            const backendDetail =
                error.response.data?.detail ||
                error.response.data?.message ||
                (typeof error.response.data === 'string' ? error.response.data : null);
            return Promise.reject({
                message: backendDetail || 'Server error. Please try again later.',
                statusCode: error.response.status,
                data: error.response.data,
            });
        }

        // Return formatted error
        return Promise.reject({
            message: error.response.data.detail || error.response.data.message || 'An error occurred',
            statusCode: error.response.status,
            data: error.response.data,
        });
    }
);

/**
 * Handle authentication failure
 * Clears tokens and redirects to login
 */
const handleAuthFailure = (error) => {
    // Issue #196: the real tokens are HttpOnly cookies now, already cleared
    // server-side by whatever 401'd (or about to expire on their own) — this
    // just drops the non-sensitive UI cache and any pre-migration leftovers
    // a browser that hasn't logged in since might still be carrying.
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    // WHY the session ended, so the login page can say it. Being signed out
    // because the account was used somewhere else is not the same event as a
    // session quietly expiring overnight, and the person deserves to know
    // which one happened — especially when the honest answer is "someone else
    // is signed in as you".
    //
    // A CODE, never the server's sentence: this value lands in a URL anyone
    // can craft, and a login page that renders arbitrary text handed to it in
    // a query string is a phishing surface. The wording is chosen locally,
    // from the fixed set in sessionEndedNotice().
    const detail = error?.response?.data?.detail;
    const reason = (detail && typeof detail === 'object' && detail.reason) || '';

    // Only redirect if not already on login/signup page
    if (!window.location.pathname.includes('/login') &&
        !window.location.pathname.includes('/signup')) {
        window.location.href = reason
            ? `/login?signed_out=${encodeURIComponent(reason)}`
            : '/login?session_expired=true';
    }
};

/**
 * Health check endpoint
 * Used to verify API connectivity
 */
export const checkApiHealth = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/health`, {
            timeout: 5000,
        });
        return response.data;
    } catch (error) {
        console.error('API health check failed:', error);
        return { status: 'unhealthy', error: error.message };
    }
};

/**
 * Get current API configuration
 * Useful for debugging
 */
export const getApiConfig = () => ({
    baseURL: API_BASE_URL,
    environment: process.env.NODE_ENV,
    timeout: apiClient.defaults.timeout,
});

export default apiClient;
